import React, { type FC, useEffect } from "react";

import { useIntl } from "react-intl";

import styled from "@emotion/styled";
import {
  Button,
  Checkbox,
  ContextMenu,
  DataListExtraColumn,
  DataListHeader,
  DataListRow,
  Flex,
  Grid,
  GridCell,
  IconArrow1Down,
  NoResults,
  PaginationBar,
  Panel,
  SkeletonLoader,
  SpacingVertical,
  Text,
  makeButtonWithMenu,
} from "mailup-web-components";
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { EXTRA_COLUMN_AFTER_WIDTH } from "../model";
import {
  downloadImportFileCallback,
  searchImportedCallback,
} from "../recoil/callbacks";
import {
  modalDelesteImpostsState,
  selectionCountState,
  selectionState,
  tableImportedState,
} from "../recoil/state";
import ImportedTableRow from "./ImportedTableRow";
import ItemSelectAll from "./ItemSelectAll";

const ImportedTable: FC = () => {
  const intl = useIntl();
  const searchImported = useRecoilCallback(searchImportedCallback, []);
  const [
    {
      loading,
      items,
      totalItems,
      pageSize,
      pageNumber,
      orderBy,
      orderDescending,
      isInit,
    },
    setTableImportedState,
  ] = useRecoilState(tableImportedState);
  const setModalDelete = useSetRecoilState(modalDelesteImpostsState);
  const { mode, checked, unchecked } = useRecoilValue(selectionState);

  const handleClickSort = (val: string) => {
    setTableImportedState((prev) => ({
      ...prev,
      orderBy: val,
      orderDescending: !orderDescending,
      pageNumber: 1,
    }));
    searchImported(false, {
      orderBy: val,
      orderDescending: !orderDescending,
      pageNumber: 1,
    });
  };

  function handleChangePage(val: number) {
    setTableImportedState((prev) => ({
      ...prev,
      pageNumber: val,
    }));
    searchImported(false, {
      pageNumber: val,
    });
  }

  function handleChangePageSize(val: number) {
    setTableImportedState((prev) => ({
      ...prev,
      pageSize: val,
    }));
    searchImported(false, {
      pageSize: val,
    });
  }

  const heads = [
    {
      label: <ItemSelectAll />,
      span: 1,
    },
    {
      field: "Filename",
      label: intl.formatMessage({
        id: "Load.FileImportStatus.FileNameHeader",
      }),
      span: 3,
      sortable: true,
    },
    {
      label: intl.formatMessage({
        id: "Load.FileImportStatus.RowHeader",
      }),
      span: 1,
      sortable: false,
    },
    {
      label: intl.formatMessage({
        id: "Load.FileImportStatus.Groups",
      }),
      span: 3,
      sortable: false,
    },
    {
      field: "StartDate",
      label: intl.formatMessage({
        id: "Load.FileImportStatus.StartDateHeader",
      }),
      span: 2,
      sortable: true,
    },
    {
      field: "Status",
      label: intl.formatMessage({
        id: "Load.FileImportStatus.StatusHeader",
      }),
      span: 2,
      sortable: true,
    },
  ];

  const ButtonComponent = () => (
    <StyledButton
      kind={"primary"}
      disabled={
        loading || !totalItems || (mode === "selection" && checked.length === 0)
      }
    >
      <span>
        {intl.formatMessage({ id: "Load.FileImportStatus.ActionHeader" })}
        <IconArrow1Down />
      </span>
    </StyledButton>
  );

  function onClickDeleteMultiple() {
    setModalDelete((_prev) => ({
      show: true,
      title: intl.formatMessage(
        {
          id: "Load.FileImportStatus.ModalDeleteTitle",
        },
        {
          X: mode === "selection" ? checked.length : totalItems,
        },
      ),
      descr: intl.formatMessage({
        id: "Load.FileImportStatus.ModalDeleteDescr",
      }),
      id: 0,
      source: "tableImported",
    }));
  }

  const MenuComponent = () => {
    const items = [
      {
        onClick: onClickDeleteMultiple,
        label: intl.formatMessage({
          id: "Load.FileImportStatus.DeleteMultiple",
        }),
        value: "Delete",
      },
    ];
    return <ContextMenu items={items} />;
  };

  const ButtonWithMenu = makeButtonWithMenu(ButtonComponent, MenuComponent);
  const selectionCount = useRecoilValue(selectionCountState);
  return (
    <Panel>
      <Flex gap="8px" alignItems="center">
        <ButtonWithMenu />
        <Text>
          {intl.formatMessage(
            {
              id: "Load.FileImportStatus.FileSelectedLabel",
            },
            {
              count: selectionCount,
              b: (chunks: any) => <strong>{chunks}</strong>,
            },
          )}
        </Text>
      </Flex>
      <SpacingVertical variant="32" />
      <DataListHeader
        heads={heads}
        afterWidth={EXTRA_COLUMN_AFTER_WIDTH}
        orderByStatus={orderBy}
        orderDescendingStatus={orderDescending}
        handleChangeColumnsOrderCallback={handleClickSort}
      />
      <div>
        {loading ? (
          Array.from({ length: totalItems < 5 ? totalItems : pageSize }).map(
            (_, i) => (
              <DataListRow key={`skeleton-${i}`}>
                <Grid cols={12}>
                  <GridCell span={1}>
                    <Checkbox disabled />
                  </GridCell>
                  <GridCell span={3}>
                    <SkeletonLoader />
                  </GridCell>
                  <GridCell span={1}>
                    <SkeletonLoader />
                  </GridCell>
                  <GridCell span={2}>
                    <SkeletonLoader />
                  </GridCell>
                  <GridCell span={2}>
                    <SkeletonLoader />
                  </GridCell>
                  <GridCell span={2}>
                    <SkeletonLoader />
                  </GridCell>
                </Grid>
                <DataListExtraColumn after width={EXTRA_COLUMN_AFTER_WIDTH} />
              </DataListRow>
            ),
          )
        ) : (
          <>
            {totalItems > 0 &&
              items.map((item, i) => <ImportedTableRow key={i} item={item} />)}
            {totalItems === 0 && (
              <NoResults
                text={intl.formatMessage({
                  id: "Load.FileImportStatus.NoResultText",
                })}
              />
            )}
          </>
        )}
      </div>

      <PaginationBar
        pageNumber={pageNumber}
        pageSize={pageSize}
        totalItems={totalItems}
        handleChangePage={handleChangePage}
        handleChangePageSize={handleChangePageSize}
      />
    </Panel>
  );
};

export default ImportedTable;

const StyledButton = styled(Button)`
 float: left !important;
 .c-svg-icon {margin-left: 8px;}
`;
